import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import moment from "moment";
import DeleteModal from "../../../../Components/Common/DeleteModal";
import EditPaymentModal from "../../../../Components/Common/EditPaymentModal";
import CreateInvoiceModal from "./CreateInvoiceModal";
import InvoiceModalPdf from "../../../../Components/Common/invoiceModalPdf";
import LinkInvoiceModal from "../../../../Components/Common/LinkInvoiceModal";
import MoneyDisplay from "./MoneyDisplay";
import { usePaymentOperations } from "../../../../Components/Hooks/usePaymentOperations";
import { useCurrencyFormatter } from "../../../../Components/Hooks/useCurrencyFormatter";
import { clearLinkInvoiceState } from "../../../../slices/smartbill/reducer";
import { linkInvoice as onLinkInvoice, unlinkInvoice as onUnlinkInvoice } from "../../../../slices/smartbill/thunk";
import {
  getContractByIdFull as onGetContractByIdFull,
} from "../../../../slices/thunks";
import { theme, fmtRelative } from "../theme";
import { PAYMENT_TYPE_LABELS } from "../../../../common/paymentTypes";

const gridCols = "32px minmax(112px,1fr) 72px minmax(92px,1fr) 84px 80px 44px";

const PaymentsLedger = ({ contract }) => {
  const dispatch = useDispatch();
  const payments = contract?.payments || [];
  const today = moment().startOf("day");

  const { linkInvoiceLoading, linkInvoiceError, linkInvoiceErrorCode } = useSelector((state) => state.SmartBill);
  const { formatCurrency } = useCurrencyFormatter();
  const {
    invoiceLoading,
    getCalculatedSettlementPrice,
    sortPaymentsByDate,
    createInvoiceForPayment,
    savePayments,
  } = usePaymentOperations();

  const [modals, setModals] = useState({ edit: false, invoice: false, linkInvoice: false, pdf: false });
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [editForm, setEditForm] = useState({ total_contract_value: 0, vat_percentage: 21 });
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
  const [selectedInvoiceName, setSelectedInvoiceName] = useState("");
  const [unlinkTarget, setUnlinkTarget] = useState(null);
  const [unlinking, setUnlinking] = useState(false);

  const toggleModal = (name) => setModals((prev) => ({ ...prev, [name]: !prev[name] }));

  const closeAllModals = () => {
    setModals({ edit: false, invoice: false, linkInvoice: false, pdf: false });
    setSelectedPayment(null);
    dispatch(clearLinkInvoiceState());
  };

  const handleOpenEditPayments = () => {
    const sortedPayments = sortPaymentsByDate(payments);
    setEditForm({
      total_contract_value: getCalculatedSettlementPrice(contract, payments),
      vat_percentage: parseInt(sortedPayments[0]?.vat_percentage || 21),
      payments: sortedPayments,
      contract,
    });
    toggleModal("edit");
  };

  const handleCreateInvoiceClick = (payment) => {
    setSelectedPayment(payment);
    toggleModal("invoice");
  };

  const handleLinkInvoiceClick = (payment) => {
    setSelectedPayment(payment);
    dispatch(clearLinkInvoiceState());
    toggleModal("linkInvoice");
  };

  const handleViewInvoice = (invoiceData) => {
    setSelectedInvoiceId(invoiceData.id || invoiceData);
    setSelectedInvoiceName(
      invoiceData.series_name && invoiceData.number
        ? `${invoiceData.series_name}/${invoiceData.number}`
        : "Invoice"
    );
    toggleModal("pdf");
  };

  const handleUnlinkInvoiceClick = (payment) => {
    setUnlinkTarget(payment);
  };

  const handleConfirmUnlinkInvoice = async () => {
    if (!unlinkTarget) return;
    try {
      setUnlinking(true);
      await dispatch(onUnlinkInvoice(unlinkTarget.id)).unwrap();
      await dispatch(onGetContractByIdFull(contract.id));
      setUnlinkTarget(null);
      toast.success("Invoice unlinked successfully", { autoClose: 3000 });
    } catch (error) {
      console.error("Error unlinking invoice:", error);
      toast.error(error?.message || "Failed to unlink invoice", { autoClose: 3000 });
    } finally {
      setUnlinking(false);
    }
  };

  const handleLinkInvoice = async (linkData) => {
    const result = await dispatch(
      onLinkInvoice({
        payment_id: linkData.payment_id,
        series_name: linkData.series_name,
        series_number: linkData.series_number,
      })
    );

    if (onLinkInvoice.fulfilled.match(result)) {
      toggleModal("linkInvoice");
      const paymentName = PAYMENT_TYPE_LABELS[selectedPayment?.type] || selectedPayment?.type;
      setSelectedPayment(null);
      toast.success(
        `Invoice ${linkData.series_name}/${linkData.series_number} linked to ${paymentName}`,
        { autoClose: 3000 }
      );
      dispatch(onGetContractByIdFull(contract.id));
    } else {
      toast.error(
        result.payload?.message || "Failed to link invoice",
        { autoClose: 3000 }
      );
    }
  };

  const handleLinkModalClose = () => {
    toggleModal("linkInvoice");
    setSelectedPayment(null);
    dispatch(clearLinkInvoiceState());
  };

  const handleCreateInvoice = async (requestData) => {
    const result = await createInvoiceForPayment(requestData);
    if (result) {
      toggleModal("invoice");
      const paymentName = PAYMENT_TYPE_LABELS[selectedPayment?.type] || selectedPayment?.type;
      setSelectedPayment(null);
      toast.success(
        `Invoice created for ${paymentName}`,
        { autoClose: 3000 }
      );
      dispatch(onGetContractByIdFull(contract.id));
    }
  };

  const handleSavePayments = async (updatedPayments, unitPrices) => {
    const result = await savePayments(updatedPayments, unitPrices, payments, contract);
    if (result.success) {
      closeAllModals();
      dispatch(onGetContractByIdFull(contract.id));
    }
    return result;
  };

  const totalScheduled = payments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
  const totalScheduledRon = payments.reduce((sum, p) => sum + (p.amount_ron || 0), 0) || null;

  const rows = payments.map((payment) => {
    const isPaid = !!payment.cashed;
    const amount = parseFloat(payment.amount) || 0;
    const share = totalScheduled > 0 ? Math.round((amount / totalScheduled) * 100) : 0;

    let state, stBg, stFg, due, dueSub, dueFg;
    if (isPaid) {
      state = "Paid";
      stBg = theme.successBg;
      stFg = theme.success;
      due = moment(payment.cashed_date || payment.booking_date).format("D MMM YYYY");
      dueSub = "settled";
      dueFg = theme.textSecondary;
    } else if (!payment.booking_date) {
      state = "Unpaid";
      stBg = theme.neutralBadgeBg;
      stFg = theme.textSecondary;
      due = "No due date";
      dueSub = "optional";
      dueFg = theme.textMuted;
    } else {
      const days = moment(payment.booking_date).startOf("day").diff(today, "days");
      const overdue = days < 0;
      state = overdue ? "Overdue" : "Unpaid";
      stBg = overdue ? theme.dangerBadgeBg : theme.accentSoftBg;
      stFg = overdue ? theme.dangerText : theme.accent;
      due = moment(payment.booking_date).format("D MMM YYYY");
      dueSub = overdue ? `${fmtRelative(-days)} late` : `in ${fmtRelative(days)}`;
      dueFg = overdue ? theme.dangerText : theme.textSecondary;
    }

    return { ...payment, isPaid, share, state, stBg, stFg, due, dueSub, dueFg };
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div
        style={{
          background: theme.surface,
          border: `1px solid ${theme.border}`,
          borderRadius: 14,
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        <div style={{ display: "flex", alignItems: "baseline", gap: 10, padding: "16px 18px 14px", flexWrap: "wrap" }}>
          <h2 style={{ margin: 0, fontSize: 15.5, fontWeight: 650, letterSpacing: "-0.2px" }}>Payments</h2>
          <span style={{ fontSize: 13, color: theme.textMuted }}>
            {payments.length} scheduled · total {formatCurrency(totalScheduled, "EUR")}
          </span>
          <button
            type="button"
            onClick={handleOpenEditPayments}
            style={{
              marginLeft: "auto",
              font: "inherit",
              fontSize: 13,
              fontWeight: 600,
              padding: "7px 13px",
              borderRadius: 8,
              color: theme.accent,
              background: theme.surface,
              border: `1px solid ${theme.accentSoftBorder}`,
              boxShadow: "0 1px 2px rgba(20,20,32,0.06)",
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            Edit schedule
          </button>
        </div>

        <div style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: gridCols,
              gap: 6,
              padding: "10px 14px",
              background: theme.surfaceSubtle,
              borderTop: "1px solid #eeeef2",
              borderBottom: "1px solid #eeeef2",
              position: "sticky",
              top: 0,
              zIndex: 1,
              fontSize: 11,
              fontWeight: 650,
              letterSpacing: "0.6px",
              textTransform: "uppercase",
              color: theme.textMuted,
            }}
          >
            <span>#</span>
            <span>Category</span>
            <span>Status</span>
            <span>Due</span>
            <span style={{ textAlign: "right" }}>Amount</span>
            <span style={{ textAlign: "right" }}>Invoice</span>
            <span style={{ textAlign: "right" }}>Action</span>
          </div>

          {rows.length === 0 ? (
            <div style={{ padding: 24, textAlign: "center", color: theme.textMuted, fontSize: 13.5 }}>
              No payments recorded for this contract
            </div>
          ) : (
            rows.map((payment, index) => (
              <div
                key={payment.id}
                style={{
                  display: "grid",
                  gridTemplateColumns: gridCols,
                  gap: 6,
                  padding: 14,
                  borderBottom: "1px solid #f2f2f6",
                  alignItems: "center",
                }}
              >
                <span style={{ fontSize: 13, color: theme.textMuted, fontVariantNumeric: "tabular-nums" }}>
                  {index + 1}
                </span>
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 600,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {PAYMENT_TYPE_LABELS[payment.type] || payment.type}
                  </div>
                  <div style={{ fontSize: 11.5, color: theme.textMuted, marginTop: 2, fontVariantNumeric: "tabular-nums" }}>
                    {payment.share}% of contract
                  </div>
                </div>
                <div>
                  <span
                    style={{
                      display: "inline-block",
                      fontSize: 10.5,
                      fontWeight: 600,
                      letterSpacing: "0.4px",
                      textTransform: "uppercase",
                      padding: "3px 8px",
                      borderRadius: 5,
                      background: payment.stBg,
                      color: payment.stFg,
                    }}
                  >
                    {payment.state}
                  </span>
                </div>
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 13.5,
                      color: payment.dueFg,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {payment.due}
                  </div>
                  <div style={{ fontSize: 11.5, color: theme.textMuted, marginTop: 2 }}>{payment.dueSub}</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <MoneyDisplay eurAmount={payment.amount} ronAmount={payment.amount_ron} size={14.5} />
                </div>
                <div style={{ textAlign: "right", minWidth: 0 }}>
                  {payment.invoice ? (
                    <button
                      type="button"
                      onClick={() => handleViewInvoice(payment.invoice)}
                      style={{
                        font: "inherit",
                        fontSize: 12.5,
                        fontWeight: 600,
                        color: theme.accent,
                        background: "none",
                        border: "none",
                        padding: 0,
                        maxWidth: "100%",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        cursor: "pointer",
                      }}
                    >
                      {payment.invoice.series_name}/{payment.invoice.number}
                    </button>
                  ) : (
                    <span style={{ fontSize: 13.5, color: theme.neutralBar }}>—</span>
                  )}
                </div>
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  {payment.invoice ? (
                    <button
                      type="button"
                      title="Remove invoice"
                      onClick={() => handleUnlinkInvoiceClick(payment)}
                      style={{
                        font: "inherit",
                        fontSize: 12,
                        lineHeight: 1,
                        width: 30,
                        height: 30,
                        border: `1px solid ${theme.dangerBadgeBg}`,
                        borderRadius: 8,
                        background: theme.surface,
                        color: theme.dangerText,
                        cursor: "pointer",
                      }}
                    >
                      ✕
                    </button>
                  ) : (
                    <UncontrolledDropdown>
                      <DropdownToggle
                        tag="button"
                        title="Add invoice"
                        style={{
                          font: "inherit",
                          fontSize: 14,
                          lineHeight: 1,
                          width: 30,
                          height: 30,
                          border: `1px solid ${theme.border}`,
                          borderRadius: 8,
                          background: theme.surface,
                          color: theme.accent,
                          cursor: "pointer",
                        }}
                      >
                        +
                      </DropdownToggle>
                      <DropdownMenu end>
                        <DropdownItem onClick={() => handleCreateInvoiceClick(payment)}>
                          <i className="ri-file-add-line align-middle me-2"></i>
                          Create invoice…
                        </DropdownItem>
                        <DropdownItem onClick={() => handleLinkInvoiceClick(payment)}>
                          <i className="ri-link align-middle me-2"></i>
                          Link existing invoice…
                        </DropdownItem>
                      </DropdownMenu>
                    </UncontrolledDropdown>
                  )}
                </div>
              </div>
            ))
          )}

          {rows.length > 0 && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: gridCols,
                gap: 6,
                padding: "13px 14px",
                background: theme.surfaceSubtle,
                alignItems: "center",
                position: "sticky",
                bottom: 0,
              }}
            >
              <span></span>
              <span style={{ fontSize: 13, fontWeight: 600, color: theme.textSecondary }}>Total scheduled</span>
              <span></span>
              <span></span>
              <div style={{ textAlign: "right" }}>
                <MoneyDisplay eurAmount={totalScheduled} ronAmount={totalScheduledRon} size={14.5} />
              </div>
              <span></span>
              <span></span>
            </div>
          )}
        </div>
      </div>

      <DeleteModal
        show={!!unlinkTarget}
        onDeleteClick={handleConfirmUnlinkInvoice}
        onCloseClick={() => setUnlinkTarget(null)}
        isDeleting={unlinking}
      />

      <EditPaymentModal
        isOpen={modals.edit}
        toggle={() => toggleModal("edit")}
        editForm={editForm}
        setEditForm={setEditForm}
        onSave={handleSavePayments}
      />

      <CreateInvoiceModal
        isOpen={modals.invoice}
        toggle={() => toggleModal("invoice")}
        payment={selectedPayment}
        contract={contract}
        onCreateInvoice={handleCreateInvoice}
        loading={invoiceLoading}
      />

      <LinkInvoiceModal
        isOpen={modals.linkInvoice}
        toggle={handleLinkModalClose}
        payment={selectedPayment}
        onLinkInvoice={handleLinkInvoice}
        onDismissError={() => dispatch(clearLinkInvoiceState())}
        loading={linkInvoiceLoading}
        serverError={linkInvoiceError}
        errorCode={linkInvoiceErrorCode}
      />

      <InvoiceModalPdf
        isOpen={modals.pdf}
        toggle={() => toggleModal("pdf")}
        invoiceId={selectedInvoiceId}
        invoiceName={selectedInvoiceName}
      />
    </div>
  );
};

export default PaymentsLedger;
